// 统一请求封装: 透传 utoken header, 解封统一响应 {code, message, data}.
// utoken 来源由 utils/auth.ts 统一管理 (passport-client SDK 写入的 cookie / localStorage).

import { auth } from '../utils/auth';
import { alertDialog } from '../utils/dialog';

const API_BASE = import.meta.env.VITE_REQUEST_BASE_URL || '';

// 给 <img src> / <a href> 这种非 fetch 用途用: 把后端返回的相对路径 (如 /v1/attachments/X/file)
// 拼上 API_BASE; 已是绝对 URL (http:// / https:// / blob:) 则原样返回.
export function absoluteURL(maybeRelative: string): string {
    if (!maybeRelative) return '';
    if (/^([a-z]+:)?\/\//i.test(maybeRelative) || maybeRelative.startsWith('blob:') || maybeRelative.startsWith('data:')) {
        return maybeRelative;
    }
    return `${API_BASE}${maybeRelative}`;
}

// 图片 src 智能解析: 后端 API 路径 (/v1/*) 走 absoluteURL 拼 API_BASE;
// 前端静态 mock 资源 (/mock-images/*) 拼上 vite base (本地是 "/", dev 是 "/material-frontend/")
// — 否则部署到子路径下时浏览器会用根路径解析, 直接 404.
// 用于 material.image / gallery.imageUrl 等可能混合来源的字段.
export function imgSrc(p: string | undefined | null): string {
    if (!p) return '';
    if (/^([a-z]+:)?\/\//i.test(p) || p.startsWith('blob:') || p.startsWith('data:')) return p;
    if (p.startsWith('/v1/')) return absoluteURL(p);
    if (p.startsWith('/mock-images/')) {
        const base = import.meta.env.BASE_URL.replace(/\/$/, '');
        return `${base}${p}`;
    }
    return p;
}

// 与 makeblock-go/utils/v2/response 错误码一致.
const CODE_OK = 0;
const CODE_TOKEN_INVALID = 20101;
const CODE_PERMISSION_DENIED = 20102;
/** 20103 未获平台准入 (AccessGate 跳申请页); 20104 角色无权访问该资源域 (RBAC). */
export const CODE_ACCESS_DENIED = 20103;
export const CODE_RBAC_DENIED = 20104;

export interface ApiEnvelope<T> {
    code: number;
    message: string;
    data: T;
}

export class ApiError extends Error {
    code: number;
    detail?: string;

    constructor(code: number, message: string, detail?: string) {
        super(message);
        this.code = code;
        this.detail = detail;
    }
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
    /** 接受任意对象, 内部按字符串序列化; null/undefined/'' 自动跳过. */
    query?: object;
    body?: unknown;
    /**
     * 并发去重: 同 (method, url, body) 的请求在**上一发还没回来**期间共享同一个 promise.
     * GET 默认开启; 读语义的 POST 接口 (本项目不少读接口用 POST 传 body) 由调用方显式打开.
     * 只在 in-flight 期间生效, 请求一结束即释放 —— 不缓存结果, 因此不会读到陈旧数据.
     * 写接口一律不要打开 (两次点击提交必须是两次真实写入).
     */
    dedupe?: boolean;
    /**
     * 超时 (毫秒, 默认 DEFAULT_TIMEOUT_MS). 慢接口 (导出、跨库大聚合) 可单独放宽。
     * 传 0 表示不设超时 —— 只给明确知道会很久的场景, 普通页面一律别关。
     */
    timeoutMs?: number;
}

/**
 * DEFAULT_TIMEOUT_MS 请求超时兜底 (2026-09-18).
 *
 * 起因: 页面"突然卡住不动"。fetch 本身**没有任何超时**, 接口挂多久 loading 就转多久 ——
 * 数据资产几个页面直连外部源库 (prism PG / c10s 分析库), 实测出现过 43s、90s 的请求,
 * 网关要 ~90s 才 503, 这期间页面只有一个转圈。
 *
 * 更糟的是 GET 默认走 in-flight 去重: 那一发还没回来时, 切筛选再切回来、重进页面都会
 * 复用同一个挂起的 promise, **Network 里看不到新请求**, 表现就是"点了完全没反应"。
 *
 * 所以宁可早失败: 30 秒还没回来就 abort, 页面报错、用户能重试 (promise settle 后
 * inflight 立即释放, 重试是一发真实请求)。后端该修的慢查询照修, 但前端不能没有兜底。
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/** CODE_TIMEOUT 前端造的错误码 (后端不会返回), 页面可据此区分"超时"与业务错误. */
export const CODE_TIMEOUT = -1;

/**
 * CODE_ABORTED 请求被主动取消 (离开页面时的 abortLiveReads), 不是超时也不是失败.
 * 与 CODE_TIMEOUT 分开是因为二者在 fetch 里都表现为 signal.aborted —— 混成一个码,
 * 一个 31ms 就被取消的请求会报成"请求超时 (30s)", 排查时直接指错方向 (2026-09-18 实发).
 * 发起页此时基本已卸载, 页面不必也不该为它弹错.
 */
export const CODE_ABORTED = -2;

function buildQuery(query?: object): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query as Record<string, unknown>).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        params.append(k, String(v));
    });
    const s = params.toString();
    return s ? `?${s}` : '';
}

/** 解封统一响应: code=0 返回完整 envelope (data + message), 否则按错误码抛 ApiError. */
async function requestEnvelope<T>(path: string, opts: RequestOptions = {}): Promise<ApiEnvelope<T>> {
    const { method = 'GET', query, body, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
    const url = `${API_BASE}${path}${buildQuery(query)}`;
    const headers: Record<string, string> = {
        Accept: 'application/json',
        utoken: auth.getUToken(),
    };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    const ctrl = timeoutMs > 0 ? new AbortController() : null;
    // timedOut 区分"谁 abort 的": 只有计时器到点才是超时, 其余都是主动取消 (见 CODE_ABORTED)
    let timedOut = false;
    const timer = ctrl
        ? window.setTimeout(() => {
              timedOut = true;
              ctrl.abort();
          }, timeoutMs)
        : 0;
    // 读请求登记进 liveReads, 路由切换时统一取消 (写请求不登记, 见 liveReads 的批注)
    const isRead = method === 'GET' || (opts.dedupe ?? false);
    if (ctrl && isRead) liveReads.add(ctrl);
    let resp: Response;
    try {
        resp = await fetch(url, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
            signal: ctrl?.signal,
        });
    } catch (e) {
        // abort 与网络错误在 fetch 里都是 reject, 分流后给用户看得懂的话
        if (timedOut) {
            throw new ApiError(CODE_TIMEOUT, `请求超时 (${Math.round(timeoutMs / 1000)}s), 请重试`);
        }
        if (ctrl?.signal.aborted) {
            throw new ApiError(CODE_ABORTED, '请求已取消');
        }
        throw e;
    } finally {
        if (timer) window.clearTimeout(timer);
        if (ctrl) liveReads.delete(ctrl);
    }
    if (!resp.ok) {
        throw new ApiError(resp.status, `HTTP ${resp.status}`);
    }
    const envelope = (await resp.json()) as ApiEnvelope<T>;
    if (envelope.code === CODE_OK) {
        return envelope;
    }
    if (envelope.code === CODE_TOKEN_INVALID) {
        // utoken 失效 → 飞书跳转登录, 登录成功后回到当前页
        auth.goToLogin();
        throw new ApiError(envelope.code, envelope.message || 'token invalid');
    }
    if (envelope.code === CODE_PERMISSION_DENIED) {
        throw new ApiError(envelope.code, envelope.message || 'permission denied');
    }
    throw new ApiError(envelope.code, envelope.message);
}

// 同参并发请求共享一发 (见 RequestOptions.dedupe): 一个页面里多个卡片同时挂载、
// 各自 useEffect 拉同一份数据时, 只打一次网络请求; 请求 settle 后立即从表里移除.
// ⚠ 去重靠的是"总会 settle": 没有超时的话, 一个挂住的请求会把后续同参请求全都钉在
// 它身上 (用户看到的就是"点了没反应"), 所以 DEFAULT_TIMEOUT_MS 是这套机制的前提.
const inflight = new Map<string, Promise<unknown>>();

/**
 * liveReads 在飞的**读**请求 (GET / dedupe 开着的读语义 POST)。
 *
 * 为什么要登记: 线上是 HTTP/2, 慢请求不会像 HTTP/1.1 那样占满连接把别的页面卡死,
 * 但**后端那条查询会一直跑下去** —— 数据资产几个页面直连外部源库, 一条全表扫能占住
 * 分析库的 IO 与 buffer pool, 用户早就切走了它还在拖别人 (实测: 商品列表 page=50 被
 * 同期一条 90s 的 options 查询拖到 19s)。所以离开页面时就该把这些读请求取消掉。
 *
 * ⚠ 只登记读: 写请求 (提交表单 / 审批 / 批量) **绝不能**因为跳转被取消 —— 那会变成
 * "点了保存, 跳转后数据没存上"。
 */
const liveReads = new Set<AbortController>();

/**
 * abortLiveReads 取消当前所有在飞的读请求 (路由切换时调用)。
 * 被取消的 promise 走 CODE_TIMEOUT 分支, 页面自己的 catch 会处理; 组件此时通常已卸载,
 * setState 不会生效, 也就不会有残留的 loading。
 */
export function abortLiveReads(): void {
    liveReads.forEach((c) => c.abort());
    liveReads.clear();
}

function dedupeKey(path: string, opts: RequestOptions): string {
    return `${opts.method || 'GET'} ${path}${buildQuery(opts.query)} ${
        opts.body === undefined ? '' : JSON.stringify(opts.body)
    }`;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method || 'GET';
    if (!(opts.dedupe ?? method === 'GET')) {
        return (await requestEnvelope<T>(path, opts)).data;
    }
    const key = dedupeKey(path, opts);
    const hit = inflight.get(key);
    if (hit) return hit as Promise<T>;
    const p = requestEnvelope<T>(path, opts)
        .then((env) => env.data)
        .finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
}

/** requestWithMsg 的返回: data + 后端 message (写接口审批流场景 message 含"已提交审批"). */
export interface MsgResult<T> {
    data: T;
    message: string;
}

/**
 * requestWithMsg 与 request 相同, 但额外透出 envelope.message.
 * 仅供写操作调用点使用: 普通用户的写请求会被后端转成审批工单
 * (code=0, message="已提交审批...", data={taskCode}), 调用方要拿 message 提示用户.
 */
export async function requestWithMsg<T>(
    path: string,
    opts: RequestOptions = {},
): Promise<MsgResult<T>> {
    const envelope = await requestEnvelope<T>(path, opts);
    return { data: envelope.data, message: envelope.message || '' };
}

/**
 * 写操作走审批流时 (message 含"已提交审批") 弹提示并返回 true:
 * 变更不会即时生效, 本地 UI 不应把它当作已生效, 重新 load 即可 (数据未变).
 */
/** 写接口返回的 message 是否表示走了审批流 (数据未即时生效). */
export function isApprovalPending(message: string | undefined): boolean {
    return !!message && message.includes('已提交审批');
}

export function alertApprovalIfPending(message: string | undefined): boolean {
    if (isApprovalPending(message)) {
        alertDialog(`${message}\n\n本次变更暂未生效, 可在「我的工单」跟踪进度, 管理员通过后自动生效.`);
        return true;
    }
    return false;
}

/**
 * downloadFile 带 utoken 下载文件 (a 标签带不了 header): fetch → blob → 临时链接触发保存.
 * 传 body 时改用 POST (导出勾选场景: xcodes 列表可能很长, 塞不进 GET query).
 *
 * 一切文件下载入口都必须走这里, 不能用 `<a href download>`: 浏览器直接跳转带不上 utoken
 * header (后端 AuthUToken 只认请求头, 没有 query 参数兜底), dev/test/prod 上必然是
 * 20101 "token验证失效" —— 本地 BYPASS_AUTH=true 把中间件整个摘掉了才看不出来.
 */
export async function downloadFile(path: string, filename: string, body?: unknown): Promise<void> {
    saveBlob(await fetchFileBlob(path, body), filename);
}

/**
 * fetchFileBlob 带 utoken 拉取文件流 (下载/预览共用): 传 body 时改用 POST.
 * 鉴权/业务失败时后端回的是 JSON envelope 而不是文件: 必须在这里分流, 否则会把
 * 一段 {"code":20101,...} 当文件存成 .xlsx, 用户打开才发现不是表格.
 * 但下载的文件本身就可能是 JSON (套件模板 bundle .json), 那时 Content-Type 同样是
 * application/json —— 只靠它分流会把正常模板当错误抛掉. 以 Content-Disposition:
 * attachment 为准: 后端下文件必带, 回 envelope 必不带.
 */
export async function fetchFileBlob(path: string, body?: unknown): Promise<Blob> {
    const headers: Record<string, string> = { utoken: auth.getUToken() };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const resp = await fetch(`${API_BASE}${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    const isAttachment = (resp.headers.get('Content-Disposition') || '').includes('attachment');
    if (!isAttachment && (resp.headers.get('Content-Type') || '').includes('application/json')) {
        const envelope = (await resp.json()) as ApiEnvelope<unknown>;
        if (envelope.code === CODE_TOKEN_INVALID) auth.goToLogin();
        throw new ApiError(envelope.code, envelope.message || 'download failed');
    }
    return resp.blob();
}

/** saveBlob 把内存里的 Blob 以指定文件名触发浏览器保存 (临时 object URL, 用完即回收). */
export function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export interface PageResult<T> {
    list: T[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
}

/**
 * uploadFile 表单文件上传专用. 把 File 用 multipart/form-data POST 上去, 透传 utoken header.
 * 不设 Content-Type, 让浏览器自动加 boundary.
 */
export async function uploadFile<T>(
    path: string,
    file: File,
    formKey = 'file',
    extraFields?: Record<string, string>,
): Promise<T> {
    return (await uploadFileWithMsg<T>(path, file, formKey, extraFields)).data;
}

/**
 * uploadFileWithMsg 与 uploadFile 相同, 但额外透出 envelope.message.
 * 供走审批流的导入接口用 (普通用户上传 → 后端建工单, message 含"已提交审批").
 * extraFields: 随文件一起提交的额外表单字段 (如参数导入的 archCode).
 */
export async function uploadFileWithMsg<T>(
    path: string,
    file: File,
    formKey = 'file',
    extraFields?: Record<string, string>,
): Promise<MsgResult<T>> {
    const url = `${API_BASE}${path}`;
    const fd = new FormData();
    fd.append(formKey, file);
    Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
    const resp = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', utoken: auth.getUToken() },
        body: fd,
    });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    const envelope = (await resp.json()) as ApiEnvelope<T>;
    if (envelope.code === CODE_OK) {
        return { data: envelope.data, message: envelope.message || '' };
    }
    if (envelope.code === CODE_TOKEN_INVALID) {
        auth.goToLogin();
        throw new ApiError(envelope.code, envelope.message || 'token invalid');
    }
    if (envelope.code === CODE_PERMISSION_DENIED) {
        throw new ApiError(envelope.code, envelope.message || 'permission denied');
    }
    throw new ApiError(envelope.code, envelope.message);
}

